import { useEffect, useState } from "react";
import {
  Alert, Avatar, Box, Button, Card, Chip, FormControlLabel, MenuItem, Snackbar, Stack,
  Switch, TextField, Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ApartmentIcon from "@mui/icons-material/Apartment";
import { settingsApi } from "../api/resources";
import { useSettings } from "../context/SettingsContext";
import { useLanguage } from "../i18n/LanguageContext";
import { LANGS } from "../i18n/translations";
import PhotoInput from "../components/PhotoInput";

export default function AjustesPage() {
  const { settings, reload } = useSettings();
  const { t, lang, setLang } = useLanguage();
  const [form, setForm] = useState(null);
  const [newCourier, setNewCourier] = useState("");
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  if (!form) return null;

  const setNotify = (k, v) => setForm((f) => ({ ...f, notify: { ...f.notify, [k]: v } }));

  const addCourier = () => {
    const val = newCourier.trim();
    if (!val) return;
    if (form.couriers.some((c) => c.toLowerCase() === val.toLowerCase())) {
      setNewCourier("");
      return;
    }
    const withoutOtro = form.couriers.filter((c) => c !== "Otro");
    setForm({ ...form, couriers: [...withoutOtro, val, "Otro"] });
    setNewCourier("");
  };
  const removeCourier = (c) => setForm({ ...form, couriers: form.couriers.filter((x) => x !== c) });

  const save = async () => {
    await settingsApi.update(form);
    reload();
    setToast(t("ajustes.saved"));
  };

  return (
    <Box>
      <Typography variant="h5">{t("ajustes.title")}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("ajustes.subtitle")}
      </Typography>

      <Card sx={{ p: 3, mb: 3 }}>
        <Typography fontWeight={600} sx={{ mb: 2 }}>
          {t("ajustes.condoSection")}
        </Typography>
        <TextField
          label={t("ajustes.condoNameLabel")}
          fullWidth
          value={form.condoName}
          onChange={(e) => setForm({ ...form, condoName: e.target.value })}
          sx={{ mb: 3 }}
        />

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
          {t("ajustes.logoLabel")}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {t("ajustes.logoHint")}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar variant="rounded" src={form.logo} sx={{ width: 56, height: 56, bgcolor: "#EAEDE3" }}>
            <ApartmentIcon />
          </Avatar>
          <PhotoInput value={form.logo ? [form.logo] : []} onChange={(arr) => setForm({ ...form, logo: arr[0] || null })} max={1} label="" />
        </Stack>
      </Card>

      <Card sx={{ p: 3, mb: 3 }}>
        <Typography fontWeight={600} sx={{ mb: 1 }}>
          {t("ajustes.languageSection")}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          {t("ajustes.languageHint")}
        </Typography>
        <TextField select value={lang} onChange={(e) => setLang(e.target.value)} sx={{ minWidth: 200 }}>
          {LANGS.map((l) => (
            <MenuItem key={l.code} value={l.code}>
              {l.label}
            </MenuItem>
          ))}
        </TextField>
      </Card>

      <Card sx={{ p: 3, mb: 3 }}>
        <Typography fontWeight={600} sx={{ mb: 1 }}>
          {t("ajustes.couriersSection")}
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("ajustes.couriersHint")}
        </Alert>
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          {form.couriers.map((c) => (
            <Chip
              key={c}
              label={c}
              onDelete={c === "Otro" ? undefined : () => removeCourier(c)}
              deleteIcon={<CloseIcon fontSize="small" />}
            />
          ))}
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            fullWidth
            value={newCourier}
            onChange={(e) => setNewCourier(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCourier()}
            placeholder={t("ajustes.couriersPlaceholder")}
          />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addCourier}>
            {t("common.add")}
          </Button>
        </Stack>
      </Card>

      <Card sx={{ p: 3 }}>
        <Typography fontWeight={600} sx={{ mb: 1 }}>
          {t("ajustes.notifySection")}
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("ajustes.notifyHint")}
        </Alert>

        <FormControlLabel
          control={<Switch checked={form.notify.enabled} onChange={(e) => setNotify("enabled", e.target.checked)} />}
          label={t("ajustes.notifyEnabled")}
          sx={{ mb: 1, display: "block" }}
        />

        <Box sx={{ opacity: form.notify.enabled ? 1 : 0.5, pointerEvents: form.notify.enabled ? "auto" : "none" }}>
          <FormControlLabel
            control={<Switch checked={form.notify.emailEnabled} onChange={(e) => setNotify("emailEnabled", e.target.checked)} />}
            label={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <AlternateEmailIcon fontSize="small" /> <span>{t("ajustes.emailEnabled")}</span>
              </Stack>
            }
            sx={{ display: "block", mb: 1 }}
          />
          <TextField
            label={t("ajustes.emailFrom")}
            fullWidth
            value={form.notify.emailFrom}
            onChange={(e) => setNotify("emailFrom", e.target.value)}
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={<Switch checked={form.notify.whatsappEnabled} onChange={(e) => setNotify("whatsappEnabled", e.target.checked)} />}
            label={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <WhatsAppIcon fontSize="small" /> <span>{t("ajustes.whatsappEnabled")}</span>
              </Stack>
            }
            sx={{ display: "block", mb: 1 }}
          />
          <TextField
            label={t("ajustes.whatsappNumber")}
            fullWidth
            value={form.notify.whatsappNumber}
            onChange={(e) => setNotify("whatsappNumber", e.target.value)}
          />
        </Box>

        <Button variant="contained" startIcon={<CheckIcon />} onClick={save} sx={{ mt: 3 }}>
          {t("ajustes.saveSettings")}
        </Button>
      </Card>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}>
        <Alert severity="success" onClose={() => setToast(null)}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
